import React, { useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import { BarChart, Globe, Heart, Users } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Particle from "../Particle";
import { Trans, useTranslation } from "react-i18next";

export default function Stats() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();


  const handleClick = () => {
    navigate("/registration#internship-section");
  };

  const handleClick_2 = () => {
    window.open(
      "https://docs.google.com/forms/d/1iwEzu53wDAe41RjEDe-gHIX9H61QikSfR2RKmyCBp_M/edit",
      "_blank",
      "noopener,noreferrer"
    );
  };
  const handleClick_3 = () => {
    window.open(
      "https://docs.google.com/forms/d/1Q2Lc15IwomBIFeSAxIfsK56xI3vkCjvKW8yXQcWQvmM/edit",
      "_blank",
      "noopener,noreferrer"
    );
  };

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const asArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") return Object.values(value);
    return [];
  };

  const iconMap = {
    youthTrained: Users,
    seniorsServed: Heart,
    countiesCities: Globe,
    ambassadorInitiatives: BarChart,
  };

  const headlineItems = asArray(t("impactStats.headline.items", { returnObjects: true }));
  const surveyItems = asArray(t("impactStats.survey.items", { returnObjects: true }));
  const byTheNumbersItems = asArray(t("impactStats.byTheNumbers.items", { returnObjects: true }));
  const voicesItems = asArray(t("impactStats.voices.items", { returnObjects: true }));
  const reachItems = asArray(t("impactStats.reach.items", { returnObjects: true }));

  return (
    <Container fluid className="about-section impact-page impact-stats-page">
      <Particle />
      <Container className="impact-container impact-stats-container">
        <header className="impact-hero">
          <h1 className="impact-hero-title">
            <Trans i18nKey="impactStats.hero.title" components={{ purple: <strong className="purple" /> }} />
          </h1>
          <p className="impact-hero-lead">
            {t("impactStats.hero.lead")}
          </p>
        </header>

        <Row className="impact-stats-top" style={{ justifyContent: "center" }}>
          <Col md={6} className="impact-stats-col">
            <div className="policy-card impact-stats-card">
              <h2 className="impact-card-title">{t("impactStats.headline.title")}</h2>
              <p className="about-text impact-card-text">
                {t("impactStats.headline.lead")}
              </p>

              <div className="impact-stats-grid" role="list" aria-label={t("impactStats.a11y.headlineListLabel")}>
                {headlineItems.map((item, index) => {
                  const Icon = iconMap[item.key] || null;
                  const tileKey = item.key || `${item.label}-${index}`;
                  return (
                  <div key={tileKey} className="impact-stat-tile" role="listitem">
                    <div className="impact-stat-icon" aria-hidden="true">
                      {Icon ? React.createElement(Icon, { size: 26 }) : null}
                    </div>
                    <div className="impact-stat-value">{item.value}</div>
                    <div className="impact-stat-label">{item.label}</div>
                  </div>
                );
                })}
              </div>
            </div>
          </Col>

          <Col md={6} className="impact-stats-col">
            <div className="policy-card impact-stats-card">
              <h2 className="impact-card-title">{t("impactStats.survey.title")}</h2>
              <p className="about-text impact-card-text">{t("impactStats.survey.lead")}</p>

              <div className="impact-survey-grid" role="list" aria-label={t("impactStats.a11y.surveyListLabel")}>
                {surveyItems.map((s, index) => (
                  <Card key={`${s.stat}-${index}`} className="policy-card impact-mini-card" role="listitem">
                    <Card.Body>
                      <div className="impact-mini-stat">{s.stat}</div>
                      <div className="impact-mini-caption">{s.caption}</div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </div>
          </Col>
        </Row>

        <section className="impact-section" aria-label={t("impactStats.a11y.voicesSectionLabel")}>
          <h2 className="impact-section-heading">{t("impactStats.voices.title")}</h2>
          <div className="impact-voices-grid" role="list">
            {voicesItems.map((v, index) => (
              <div key={`${v.name}-${index}`} className="policy-card impact-voice-card" role="listitem">
                <p className="impact-voice-quote">“{v.quote}”</p>
                <p className="impact-voice-name">— {v.name}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="impact-section" aria-label={t("impactStats.a11y.byTheNumbersSectionLabel")}>
          <Row className="impact-stats-bottom" style={{ justifyContent: "center" }}>
            <Col lg={8} className="impact-stats-col">
              <div className="policy-card impact-stats-card">
                <h2 className="impact-card-title">{t("impactStats.byTheNumbers.title")}</h2>
                <p className="about-text impact-card-text">
                  {t("impactStats.byTheNumbers.lead")}
                </p>

                <div className="impact-byn-grid" role="list" aria-label={t("impactStats.a11y.byTheNumbersListLabel")}>
                  {byTheNumbersItems.map((b, index) => (
                    <div key={`${b.label}-${index}`} className="impact-byn-tile" role="listitem">
                      <div className="impact-byn-value">{b.value}</div>
                      <div className="impact-byn-label">{b.label}</div>
                    </div>
                  ))}
                </div>

                <div className="impact-projects">
                  <h3 className="impact-subheading">{t("impactStats.projects.title")}</h3>
                  <p className="about-text impact-body">
                    {t("impactStats.projects.body")}
                  </p>
                  <Button onClick={handleClick} className="custom-yellow-btn impact-apply-btn">
                    {t("impactStats.projects.cta")}
                  </Button>
                </div>
              </div>
            </Col>

            <Col lg={4} className="impact-stats-col">
              <div className="policy-card impact-stats-card">
                <h3 className="impact-card-title">{t("impactStats.reach.title")}</h3>
                <ul className="impact-reach">
                  {reachItems.map((text, index) => (
                    <li key={`${index}-${String(text).slice(0, 24)}`}>{text}</li>
                  ))}
                </ul>
              </div>
            </Col>
          </Row>
        </section>

        <section className="impact-section" aria-label={t("impactStats.a11y.partnerSectionLabel")}>
          <div className="policy-card impact-partner-cta">
            <div className="impact-partner-left">
              <h3 className="impact-card-title">{t("impactStats.partner.title")}</h3>
              <p className="about-text impact-card-text">
                {t("impactStats.partner.lead")}
              </p>
            </div>
            <div className="impact-partner-right">
              <Button onClick={handleClick_2} className="custom-yellow-btn impact-apply-btn">
                {t("impactStats.partner.ctaPartner")}
              </Button>
              <Button onClick={handleClick_3} className="custom-yellow-btn impact-apply-btn impact-apply-btn-secondary">
                {t("impactStats.partner.ctaVolunteer")}
              </Button>
            </div>
          </div>
        </section>

        <footer className="impact-stats-footer">
          {t("impactStats.footer")}
        </footer>
      </Container>
    </Container>
  );
}
